<template>
    <UIDialog v-if="open" :open="open" @update:open="handleOpenChange">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>{{ dialogTitle }}</DialogTitle>
                <DialogDescription>
                    {{ dialogDescription }}
                </DialogDescription>
            </DialogHeader>

            <div v-if="requiresName" class="space-y-2">
                <label class="text-sm font-medium text-slate-700" for="list-action-name">
                    {{ inputLabel }}
                </label>
                <Input
                    id="list-action-name"
                    ref="nameInput"
                    v-model="name"
                    type="text"
                    :placeholder="inputPlaceholder"
                    :class="{ 'border-red-500 ring-red-500': errorMessage }"
                    @input="resetError"
                    @keydown.enter.prevent="handleSubmit"
                />
                <p v-if="errorMessage" class="text-xs text-red-700">
                    {{ errorMessage }}
                </p>
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" :disabled="isSubmitting" @click="handleCancel">
                    Cancel
                </Button>
                <Button
                    type="button"
                    :variant="type === 'delete' ? 'destructive' : 'default'"
                    :disabled="isSubmitDisabled"
                    @click="handleSubmit"
                >
                    <AppIcon
                        v-if="isSubmitting"
                        name="spinner-third"
                        class="mr-2 h-4 w-4 animate-spin"
                    />
                    {{ submitLabel }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import {
    Dialog as UIDialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        default: 'create',
        validator: (value) => ['create', 'update', 'delete', 'copy'].includes(value),
    },
    list: {
        type: Object,
        default: null,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close', 'cancel', 'submit'])

const name = ref('')
const errorMessage = ref('')
const nameInput = ref(null)

const requiresName = computed(() => ['create', 'update', 'copy'].includes(props.type))
const trimmedName = computed(() => name.value.trim())
const listName = computed(() => props.list?.name || 'this list')

const dialogTitle = computed(() => ({
    create: 'Create List',
    update: 'Edit List',
    delete: 'Delete List',
    copy: 'Copy List',
}[props.type]))

const dialogDescription = computed(() => ({
    create: 'Create a list to collect creator profiles.',
    update: 'Rename this list.',
    delete: 'Are you sure you want to delete this list? This action cannot be undone.',
    copy: `Create a private copy of "${listName.value}" with the same creators.`,
}[props.type]))

const inputLabel = computed(() => (props.type === 'update' ? 'List Name' : 'New List Name'))
const inputPlaceholder = computed(() => (props.type === 'copy' ? 'Enter copied list name' : 'Enter list name'))
const submitLabel = computed(() => ({
    create: 'Create',
    update: 'Save',
    delete: 'Delete',
    copy: 'Copy',
}[props.type]))

const isSubmitDisabled = computed(() => (
    props.isSubmitting ||
    (requiresName.value && !trimmedName.value)
))

const resetError = () => {
    errorMessage.value = ''
}

const resetState = () => {
    name.value = ''
    resetError()
}

const setInitialName = () => {
    if (props.type === 'update') {
        name.value = props.list?.name || ''
        return
    }

    if (props.type === 'copy') {
        name.value = ''
        return
    }

    name.value = ''
}

const handleClose = () => {
    resetState()
    emit('close')
}

const handleOpenChange = (isOpen) => {
    if (props.isSubmitting) return

    if (!isOpen) {
        handleClose()
    }
}

const handleCancel = () => {
    if (props.isSubmitting) return

    resetState()
    emit('cancel')
    emit('close')
}

const handleSubmit = () => {
    if (props.isSubmitting) return

    if (requiresName.value && !trimmedName.value) {
        errorMessage.value = 'List name is required.'
        return
    }

    emit('submit', {
        type: props.type,
        list: props.list,
        name: trimmedName.value,
    })
}

watch(
    () => [props.open, props.type, props.list?.id, props.list?.name],
    async () => {
        if (!props.open) return

        resetError()
        setInitialName()
        await nextTick()

        const el = nameInput.value?.$el
        if (el) {
            el.focus()
            el.setSelectionRange(el.value.length, el.value.length)
        }
    },
    { immediate: true },
)
</script>
